import type { LevelProgress } from '../leveling';

export type FMKSlotKind = 'image' | 'text';
export type FMKPostType = 'straight' | 'trans' | 'gay';
export const FMK_DEFAULT_POST_TYPE: FMKPostType = 'straight';
export const FMK_POST_TYPES: FMKPostType[] = ['straight', 'trans', 'gay'];

export const isFMKPostType = (value: unknown): value is FMKPostType =>
  typeof value === 'string' && FMK_POST_TYPES.includes(value as FMKPostType);

export const normalizeFMKPostType = (value: unknown): FMKPostType =>
  isFMKPostType(value) ? value : FMK_DEFAULT_POST_TYPE;

export type FMKSlot = {
  id: string;
  kind: FMKSlotKind;
  value: string;
  label?: string;
  faceValue?: string;
};

export type FMKEntry = {
  id: string;
  authorId: string;
  authorUsername?: string;
  subreddit: string;
  slots: [FMKSlot, FMKSlot, FMKSlot];
  createdAt: number;
  title?: string;
  type?: FMKPostType;
};

export type AuthUser = {
  userId: string;
  authenticated: boolean;
  provider: 'google.com' | 'password' | 'anonymous' | 'unknown';
  email?: string;
  username?: string;
  hasUsername?: boolean;
};

export type AuthProfileResponse = {
  currentUser: AuthUser;
};

export type ClaimUsernameRequest = {
  username: string;
};

export type ClaimUsernameResponse = {
  currentUser: AuthUser;
};

export type PostActor = 'user' | 'app';

export type FMKAssignment = {
  f: string;
  m: string;
  k: string;
};

export type RoleTotals = {
  f: Record<string, number>;
  m: Record<string, number>;
  k: Record<string, number>;
};

export type FMKPercentages = {
  f: Record<string, number>;
  m: Record<string, number>;
  k: Record<string, number>;
};

export type FMKStats = {
  totals: RoleTotals;
  totalVotes: number;
};

export type MarryGalleryItem = {
  id: string;
  postId: string;
  slotId: string;
  label: string;
  image: string;
  faceValue?: string;
  createdAt: number;
};

export type KillWaveState = {
  waveNumber: number;
  nextWaveAt: number;
};

export type GalleryCurrencyState = {
  gold: number;
  gems: number;
  affection: Record<string, number>;
};

export type CardRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export type CardAbility = 'healer' | 'tank' | 'ranger';

export type CardRoleCounts = {
  f: number;
  m: number;
  k: number;
};

export type CardCandidate = {
  id: string;
  postId: string;
  slotId: string;
  type?: FMKPostType;
  imageSlotIndex?: number;
  label: string;
  image: string;
  faceValue?: string;
  sourcePostTitle?: string;
  sourceAuthorUsername?: string;
  sourceCreatedAt: number;
  totalVotes: number;
  popularScore: number;
  popularRank?: number;
  roleCounts: CardRoleCounts;
};

export type OwnedCard = {
  cardId: string;
  postId: string;
  slotId: string;
  baseName: string;
  // Icon and Chaos are accepted as legacy values and normalized out on load/backfill.
  adjective: 'Heartbreaker' | 'Beloved' | 'Menace' | 'Chaos' | 'Icon';
  displayName: string;
  image: string;
  faceValue?: string;
  sourcePostTitle?: string;
  rarity: CardRarity;
  ability: CardAbility;
  power: number;
  copies: number;
  firstPulledAt: number;
  updatedAt: number;
};

export type CardCollectionResponse = {
  cards: OwnedCard[];
  unopenedPacks: number;
  openedPacks: number;
  updatedAt: number;
};

export type OpenCardPackResponse = CardCollectionResponse & {
  pulledCards: OwnedCard[];
};

export type OpenCardPackRequest = {
  tutorial?: boolean;
};

export type CardTutorialStatus = 'not_started' | 'active' | 'completed';

export type CardTutorialState = {
  status: CardTutorialStatus;
  startedAt: number | null;
  completedAt: number | null;
  goldGrantedAt: number | null;
};

export type CardTutorialStartResponse = ShopStateResponse & {
  tutorial: CardTutorialState;
  grantedGold: number;
};

export type CardTutorialCompleteResponse = {
  tutorial: CardTutorialState;
};

export type StartBattleRequest = {
  tutorial?: boolean;
};

export type DailyQuestId =
  | 'play_one_ranked_battle'
  | 'open_one_pack'
  | 'interact_with_companion'
  | 'play_kill_mini_game';

export type DailyQuestState = {
  id: DailyQuestId;
  title: string;
  description: string;
  rewardGold: number;
  completed: boolean;
  claimed: boolean;
  completedAt: number | null;
  claimedAt: number | null;
};

export type QuestsStateResponse = {
  quests: DailyQuestState[];
  currency: GalleryCurrencyState;
  day: string;
};

export type ClaimQuestRequest = {
  questId: DailyQuestId;
};

export type ClaimQuestResponse = QuestsStateResponse & {
  questId: DailyQuestId;
  rewardedGold: number;
};

export type ShopDailyGiftState = {
  available: boolean;
  nextClaimAt: number;
  lastClaimedAt: number | null;
  rewardGold: number;
  rewardGems: number;
};

export type ShopAdRewardKind = 'gold' | 'gems';

export type ShopAdRewardState = {
  available: boolean;
  nextClaimAt: number;
  lastClaimedAt: number | null;
  rewardGold: number;
  rewardGems: number;
};

export type ShopStateResponse = {
  currency: GalleryCurrencyState;
  unopenedPacks: number;
  dailyGift: ShopDailyGiftState;
  adRewards: Record<ShopAdRewardKind, ShopAdRewardState>;
};

export type ShopPaidOfferKind = 'special' | 'gems';

export type ShopPaidOfferReward = {
  gold: number;
  gems: number;
  packs: number;
  guaranteedLegendaryCards?: number;
};

export type ShopPaidOffer = {
  id: string;
  kind: ShopPaidOfferKind;
  label: string;
  priceCents: number;
  currency: 'USD';
  reward: ShopPaidOfferReward;
};

export type ShopCheckoutOrderStatus = 'pending' | 'paid' | 'failed' | 'partially_paid';

export type ShopCheckoutRequest = {
  offerId: string;
};

export type ShopCheckoutResponse = {
  status: 'ok';
  orderId: string;
  invoiceId: string;
  invoiceUrl: string;
  offer: ShopPaidOffer;
};

export type ShopCheckoutStatusResponse = {
  status: 'ok';
  orderId: string;
  orderStatus: ShopCheckoutOrderStatus;
  offer: ShopPaidOffer;
  invoiceId?: string;
  invoiceUrl?: string;
  message?: string;
  shopState?: ShopStateResponse;
};

export type ShopDailyGiftResponse = ShopStateResponse & {
  claimed: boolean;
};

export type ShopClaimAdRewardRequest = {
  rewardKind: ShopAdRewardKind;
};

export type ShopClaimAdRewardResponse = ShopStateResponse & {
  rewardKind: ShopAdRewardKind;
  rewardedGold: number;
  rewardedGems: number;
};

export type ShopPurchasePacksRequest = {
  packCount: number;
};

export type ShopPurchasePacksResponse = ShopStateResponse & {
  purchasedPacks: number;
  spentGems: number;
};

export type PurchaseGoldPackResponse = ShopStateResponse & {
  purchasedPacks: number;
  spentGold: number;
};

export type DebugGrantCurrencyRequest = {
  gold?: number;
  gems?: number;
};

export type DebugGrantCurrencyResponse = ShopStateResponse & {
  grantedGold: number;
  grantedGems: number;
};

export type BattleRankProfile = {
  userId: string;
  rating: number;
  wins: number;
  losses: number;
  rank: number | null;
};

export type CardDeckState = {
  cardIds: string[];
  cards: OwnedCard[];
  updatedAt: number;
  valid: boolean;
  issues: string[];
};

export type GetCardDeckResponse = {
  deck: CardDeckState;
  collection: CardCollectionResponse;
};

export type SaveCardDeckRequest = {
  cardIds: string[];
};

export type SaveCardDeckResponse = {
  deck: CardDeckState;
  rank: BattleRankProfile;
};

export type BattleSide = 'player' | 'opponent';

export type BattleStatus = 'active' | 'won' | 'lost' | 'forfeited';

export type BattleCard = {
  instanceId: string;
  cardId: string;
  displayName: string;
  image: string;
  rarity: CardRarity;
  ability: CardAbility;
  basePower: number;
  maxPower: number;
  currentPower: number;
  destroyed: boolean;
  drawnTurn: number;
};

export type BattlePlayerState = {
  userId: string;
  displayName: string;
  rating: number;
  active: Array<BattleCard | null>;
  deck: BattleCard[];
  discard: BattleCard[];
};

export type BattleEvent = {
  id: string;
  message: string;
  createdAt: number;
};

export type BattleReward = {
  gold: number;
  xp: number;
  ratingDelta: number;
  rating: number;
};

export type BattleMatchState = {
  battleId: string;
  status: BattleStatus;
  turn: BattleSide;
  turnNumber: number;
  player: BattlePlayerState;
  opponent: BattlePlayerState;
  winner?: BattleSide;
  reward?: BattleReward;
  events: BattleEvent[];
  createdAt: number;
  updatedAt: number;
};

export type GetBattleRankResponse = {
  rank: BattleRankProfile;
  deck: CardDeckState;
};

export type BattleLeaderboardEntry = BattleRankProfile & {
  rank: number;
};

export type BattleLeaderboardResponse = {
  entries: BattleLeaderboardEntry[];
  currentUser: BattleRankProfile;
};

export type StartBattleResponse = {
  battle: BattleMatchState;
  rank: BattleRankProfile;
  matchmakingNames: string[];
};

export type GetActiveBattleResponse = {
  battle: BattleMatchState | null;
  rank: BattleRankProfile;
  deck: CardDeckState;
};

export type BattleAttackRequest = {
  attackerInstanceId: string;
  targetInstanceId: string;
};

export type BattleAnimationStep = {
  side: BattleSide;
  attackerInstanceId: string;
  targetInstanceId: string;
  targetDamage: number;
  battleBefore: BattleMatchState;
  battleAfter: BattleMatchState;
};

export type BattleActionResponse = {
  battle: BattleMatchState;
  rank: BattleRankProfile;
  userLevel?: UserLevelState;
  gainedXp: number;
  animationSteps?: BattleAnimationStep[];
};

export type FunGalleryCurrencyState = {
  gold: number;
  gems: number;
  affection: Record<string, number>;
};

export type GalleryOutfitId = 'default' | 'black' | 'rainbow' | 'gold';

export type GalleryOutfitState = {
  unlocked: Record<string, GalleryOutfitId[]>;
  selected: Record<string, GalleryOutfitId>;
};

export type CreateFMKRequest = {
  slots: Array<{ kind: FMKSlotKind; value: string; label?: string; faceValue?: string }>;
  title: string;
  type?: FMKPostType;
};

export type CreateFMKResponse = {
  entry: FMKEntry;
  postId: string;
  postUrl: string;
  postedAs: PostActor;
  postedAsReason?: string;
  userLevel: UserLevelState;
  gainedXp: number;
  xpReason?: string;
  isSubscribed?: boolean;
  showJoinPrompt?: boolean;
};

export type GetFMKResponse = {
  entry: FMKEntry | null;
  percentages: FMKPercentages;
  hasVoted: boolean;
  totalVotes: number;
  userLevel: UserLevelState | null;
  isSubscribed?: boolean;
  audioMuted?: boolean;
  assignment?: FMKAssignment | null;
  marryGallery?: MarryGalleryItem[];
  currency?: GalleryCurrencyState;
  outfits?: GalleryOutfitState;
  slots?: GallerySlotState;
  funGallery?: MarryGalleryItem[];
  funOutfits?: GalleryOutfitState;
  funSlots?: GallerySlotState;
  funCurrency?: FunGalleryCurrencyState;
  killGallery?: MarryGalleryItem[];
  selectableMarryIds?: string[];
  selectableFunIds?: string[];
  currentUser?: AuthUser;
};

export type FMKPostSort = 'new' | 'popular';

export type PostVoteValue = -1 | 0 | 1;

export type FMKPostSummaryPreview = {
  slotId?: string;
  kind: FMKSlotKind;
  value: string;
  label: string;
};

export type FMKPostSummary = {
  postId: string;
  title?: string;
  authorId?: string;
  authorUsername?: string;
  createdAt: number;
  type: FMKPostType;
  totalVotes: number;
  commentCount: number;
  popularScore?: number;
  previewKind: FMKSlotKind;
  previewValue: string;
  previewLabel: string;
  previews: FMKPostSummaryPreview[];
  userVote?: PostVoteValue;
};

export type PostVoteRequest = {
  postId: string;
  vote: Exclude<PostVoteValue, 0>;
};

export type PostVoteResponse = {
  postId: string;
  userVote: PostVoteValue;
  popularScore: number;
};

export const REPORT_REASONS = [
  'violent_illegal',
  'wrong_category',
  'copyright',
  'harassment',
  'spam_scam',
  'non_consensual_explicit',
  'underage_safety',
  'other',
] as const;

export type ReportReason = (typeof REPORT_REASONS)[number];

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  violent_illegal: 'Violent/illegal content',
  wrong_category: 'Wrong category',
  copyright: 'Copyright content',
  harassment: 'Harassment',
  spam_scam: 'Spam/scam',
  non_consensual_explicit: 'Non-consensual/explicit content',
  underage_safety: 'Underage-safety concern',
  other: 'Other',
};

export const REPORT_COMMENT_MAX_LENGTH = 500;

export const isReportReason = (value: unknown): value is ReportReason =>
  typeof value === 'string' && REPORT_REASONS.includes(value as ReportReason);

export const normalizeReportReason = (value: unknown): ReportReason =>
  isReportReason(value) ? value : 'other';

export type ReportPostRequest = {
  postId: string;
  reason?: ReportReason;
  comment?: string;
};

export type ReportPostResponse = {
  postId: string;
  reportCount: number;
  alreadyReported: boolean;
};

export type FMKPostComment = {
  id: string;
  postId: string;
  subreddit: string;
  authorId: string;
  authorUsername?: string;
  body: string;
  createdAt: number;
};

export type GetPostCommentsResponse = {
  items: FMKPostComment[];
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export type CreatePostCommentRequest = {
  postId: string;
  body: string;
};

export type CreatePostCommentResponse = {
  comment: FMKPostComment;
};

export type AdminReportItem = {
  postId: string;
  title?: string;
  subreddit: string;
  createdAt: number;
  type: FMKPostType;
  reportCount: number;
  lastReportedAt: number;
  removed: boolean;
  reasonCounts: Partial<Record<ReportReason, number>>;
  recentReports: Array<{
    reason: ReportReason;
    comment?: string;
    createdAt: number;
  }>;
};

export type AdminListReportedPostsResponse = {
  items: AdminReportItem[];
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export type AdminRemovePostRequest = {
  postId: string;
  reason?: string;
  hardDelete?: boolean;
};

export type AdminRemovePostResponse = {
  postId: string;
  removed: true;
  alreadyRemoved: boolean;
  hardDeleted?: boolean;
  deletedKeyCount?: number;
  deletedImageCount?: number;
};

export type AdminDismissPostReportsRequest = {
  postId: string;
};

export type AdminDismissPostReportsResponse = {
  postId: string;
  dismissed: true;
  dismissedReportCount: number;
};

export type AdminUpdatePostTypeRequest = {
  postId: string;
  type?: FMKPostType;
};

export type AdminUpdatePostTypeResponse = {
  postId: string;
  type: FMKPostType;
};

export type GetFMKPostsResponse = {
  items: FMKPostSummary[];
  sort: FMKPostSort;
  type: FMKPostType;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export type TournamentCategory = 'f' | 'm' | 'k';
export const TOURNAMENT_CATEGORIES: TournamentCategory[] = ['f', 'm', 'k'];

export type TournamentStage = 'round1' | 'round2' | 'round3' | 'round4';
export type TournamentStatus = 'pending' | 'active' | 'completed';

export type TournamentEntrant = {
  entrantId: string;
  postId: string;
  slotId: string;
  seed: number;
  kind: FMKSlotKind;
  value: string;
  label: string;
  faceValue?: string;
  sourcePostTitle?: string;
  sourceAuthorUsername?: string;
  sourceCreatedAt: number;
};

export type TournamentMatchupPair = {
  pairKey: string;
  leftEntrantId: string;
  rightEntrantId: string;
};

export type TournamentCategoryStanding = {
  entrantId: string;
  score: number;
  rank: number;
};

export type TournamentCategoryWinner = {
  category: TournamentCategory;
  entrantId: string;
  score: number;
};

export type TournamentWindowSummary = {
  battleId: string;
  stage: TournamentStage;
  stageIndex: number;
  groupLabel: string;
  startsAt: number;
  endsAt: number;
  entrantIds: string[];
  activeCategory?: TournamentCategory;
  phaseIndex?: number;
  phaseStartsAt?: number;
  phaseEndsAt?: number;
};

export type TournamentWindowResult = TournamentWindowSummary & {
  totalBallots: number;
  standings: Record<TournamentCategory, TournamentCategoryStanding[]>;
  winners: TournamentCategoryWinner[];
};

export type TournamentBracketSlotStatus = 'known' | 'winner' | 'current' | 'tbd';

export type TournamentBracketSlot = {
  slotId: string;
  status: TournamentBracketSlotStatus;
  entrantId?: string;
  label: string;
  thumbnailUrl?: string;
  category?: TournamentCategory;
  seed?: number;
  sourceBattleId?: string;
};

export type TournamentBracketWindowStatus = 'completed' | 'active' | 'upcoming';

export type TournamentBracketWindow = {
  battleId: string;
  stage: TournamentStage;
  stageIndex: number;
  label: string;
  status: TournamentBracketWindowStatus;
  startsAt: number;
  endsAt: number;
  activeCategory?: TournamentCategory;
  slots: TournamentBracketSlot[];
  winners: TournamentCategoryWinner[];
  totalBallots: number;
};

export type TournamentBracketRound = {
  stage: TournamentStage;
  label: string;
  windows: TournamentBracketWindow[];
};

export type TournamentBracket = {
  rounds: TournamentBracketRound[];
};

export type TournamentProgressStep = {
  stage: TournamentStage;
  label: string;
  totalWindows: number;
  completedWindows: number;
  activeWindowIndex: number | null;
  entrantsRemaining: number;
};

export type TournamentSummary = {
  weekId: string;
  startsAt: number;
  endsAt: number;
  status: TournamentStatus;
  totalEntrants: number;
  totalSourcePosts: number;
  entrants: TournamentEntrant[];
  activeWindow: TournamentWindowSummary | null;
  nextWindow: TournamentWindowSummary | null;
  completedWindowCount: number;
  totalWindowCount: number;
  progress: TournamentProgressStep[];
  finalWinners: TournamentCategoryWinner[];
};

export type GetCurrentTournamentResponse = {
  tournament: TournamentSummary | null;
};

export type TournamentRoundPayload = TournamentWindowSummary & {
  category: TournamentCategory;
  entrants: TournamentEntrant[];
  pairs: TournamentMatchupPair[];
};

export type GetTournamentRoundResponse = {
  tournament: TournamentSummary | null;
  round: TournamentRoundPayload | null;
  hasVoted: boolean;
  results: TournamentWindowResult | null;
};

export type TournamentPairVote = {
  pairKey: string;
  leftEntrantId: string;
  rightEntrantId: string;
  winnerEntrantId: string;
};

export type TournamentVoteRequest = {
  battleId: string;
  category: TournamentCategory;
  picks: TournamentPairVote[];
};

export type TournamentVoteResponse = {
  battleId: string;
  category: TournamentCategory;
  hasVoted: true;
  tournament: TournamentSummary;
  results: TournamentWindowResult;
  award: boolean;
  gainedXp: number;
  userLevel: UserLevelState;
  xpReason?: string;
};

export type GetTournamentResultsResponse = {
  tournament: TournamentSummary | null;
  windows: TournamentWindowResult[];
  finalWinners: TournamentCategoryWinner[];
  bracket: TournamentBracket | null;
};

export type GallerySelectableResponse = {
  selectableMarryIds: string[];
  selectableFunIds: string[];
  marryGallery?: MarryGalleryItem[];
  funGallery?: MarryGalleryItem[];
};

export type GalleryOutfitActionRequest = {
  characterId: string;
  outfitId: GalleryOutfitId;
};

export type GalleryOutfitActionResponse = {
  currency: GalleryCurrencyState;
  outfits: GalleryOutfitState;
};

export type GallerySlotState = {
  availableSlots: number;
};

export type GallerySlotUnlockResponse = {
  availableSlots: number;
  currency: GalleryCurrencyState;
};

export type FunGallerySlotUnlockResponse = {
  availableSlots: number;
  currency: FunGalleryCurrencyState;
};

export type FunGalleryState = {
  funGallery?: MarryGalleryItem[];
  funOutfits?: GalleryOutfitState;
  funSlots?: GallerySlotState;
};

export type GalleryActionRequest = {
  characterId: string;
};

export type GalleryActionResponse = {
  currency: GalleryCurrencyState;
  marryGallery: MarryGalleryItem[];
};

export type FunGalleryActionResponse = {
  currency: FunGalleryCurrencyState;
  funGallery: MarryGalleryItem[];
};

export type GalleryGiftResponse = {
  currency: GalleryCurrencyState;
};

export type FunGalleryGiftResponse = {
  currency: FunGalleryCurrencyState;
};

export type VoteRequest = {
  assignment: FMKAssignment;
};

export type CardPackUnlockPrompt = {
  source: 'two_vote_unlock';
  packCount: number;
  unopenedPacks: number;
  remainingResultShows: number;
};

export type VoteResponse = {
  award: boolean;
  percentages: FMKPercentages;
  majority: FMKAssignment;
  totalVotes: number;
  gainedXp: number;
  userLevel: UserLevelState;
  xpReason?: string;
  isSubscribed?: boolean;
  showJoinPrompt?: boolean;
  cardPackUnlockPrompt?: CardPackUnlockPrompt;
  marryGallery?: MarryGalleryItem[];
  funGallery?: MarryGalleryItem[];
  killGallery?: MarryGalleryItem[];
};

export type GooExitRewardRequest = {
  postId: string;
};

export type GooExitRewardResponse = {
  status: 'ok';
  awarded: boolean;
  gainedXp: number;
  userLevel: UserLevelState;
  xpReason?: string;
};

export type AudioPreferenceRequest = {
  muted: boolean;
};

export type AudioPreferenceResponse = {
  audioMuted: boolean;
};

export type KillWaveStateResponse = {
  killGallery: MarryGalleryItem[];
  waveState: KillWaveState;
  currency: GalleryCurrencyState;
};

export type KillWaveStartResponse = {
  waveState: KillWaveState;
  startedAt: number;
};

export type KillWaveCompleteRequest = {
  result: 'win' | 'lose';
};

export type KillWaveCompleteResponse = {
  waveState: KillWaveState;
  currency: GalleryCurrencyState;
  rewardedGold: number;
};

export const ANALYTICS_EVENT_NAMES = [
  'page_view',
  'poll_vote_submit',
  'result_role_previous',
  'result_role_next',
  'gallery_open',
  'gallery_close',
  'gallery_tab_change',
  'gallery_back_rub',
  'gallery_foot_rub',
  'gallery_gift',
  'gallery_divorce',
  'gallery_outfit_unlock',
  'gallery_outfit_select',
  'gallery_slot_unlock',
  'kill_wave_start',
  'kill_wave_complete',
  'goo_game_start',
  'goo_done_press',
  'goo_reward_awarded',
  'browse_open_post',
  'create_submit_success',
  'tournament_open',
  'tournament_choice_select',
  'tournament_vote_submit',
] as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENT_NAMES)[number];

export const isAnalyticsEventName = (value: unknown): value is AnalyticsEventName =>
  typeof value === 'string' && (ANALYTICS_EVENT_NAMES as readonly string[]).includes(value);

export type AnalyticsEventPayload = {
  name: AnalyticsEventName;
  properties?: Record<string, string | number | boolean | null>;
  occurredAt?: number;
};

export type TrackAnalyticsEventsRequest = {
  events: AnalyticsEventPayload[];
  tenantId?: string;
  gameId?: string;
};

export type TrackAnalyticsEventsResponse = {
  status: 'ok';
  accepted: number;
};

export type AnalyticsDailyPoint = {
  date: string;
  uniqueUsers: number;
  totalEvents: number;
};

export type AnalyticsMetric = {
  name: string;
  count: number;
};

export type AdminAnalyticsSummaryResponse = {
  days: number;
  totals: {
    uniqueUsers: number;
    totalEvents: number;
    gooStarts: number;
    gooDonePresses: number;
    gooRewards: number;
    killWaveStarts: number;
    killWaveCompletions: number;
    killWaveWins: number;
    killWaveWinRate: number;
    marryFootRubs: number;
    funBackRubs: number;
    tournamentPlays: number;
    tournamentChoices: number;
  };
  daily: AnalyticsDailyPoint[];
  events: AnalyticsMetric[];
  pages: AnalyticsMetric[];
  galleries: AnalyticsMetric[];
};

export type LeaderboardEntry = {
  userId: string;
  username?: string;
  points: number;
  rank: number;
  battleRating?: number;
};

export type LeaderboardResponse = {
  entries: LeaderboardEntry[];
  currentUserId: string;
};

export type ErrorResponse = {
  status: 'error';
  message: string;
};

export type RedditAuthStatusResponse = {
  authenticated: boolean;
  username?: string;
};

export type RedditShareRequest = {
  title: string;
  imageDataUrl: string;
  type?: FMKPostType;
};

export type RedditShareResponse = {
  postUrl: string;
};

export type UploadShareImageRequest = {
  imageDataUrl: string;
  sourcePostUrl: string;
  title?: string;
};

export type UploadShareImageResponse = {
  imageUrl: string;
  shareUrl: string;
  shareId?: string;
};

export type FaceSwapTargetType = 'image' | 'video';

export type FaceSwapJobStatus =
  | 'queued'
  | 'running'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'canceled'
  | 'expired';

export type FaceSwapErrorCode =
  | 'invalid_input'
  | 'quota_exceeded'
  | 'subscription_required'
  | 'policy_not_accepted'
  | 'rate_limited'
  | 'worker_failure'
  | 'storage_failure'
  | 'internal_error';

export type FaceSwapCreateJobRequest = {
  sourceImageUrl: string;
  targetUrl: string;
  targetType: FaceSwapTargetType;
  estimatedUsageSeconds?: number;
  options?: {
    manyFaces?: boolean;
    keepFps?: boolean;
    outputVideoQuality?: number;
    outputVideoEncoder?: 'libx264' | 'libx265' | 'h264_nvenc' | 'hevc_nvenc' | 'libvpx-vp9';
  };
  consentAccepted: boolean;
};

export type FaceSwapUploadRequest = {
  dataUrl: string;
  kind: 'source' | 'target';
  targetType?: FaceSwapTargetType;
};

export type FaceSwapUploadResponse = {
  mediaUrl: string;
  contentType: string;
};

export type FaceSwapUsageLedger = {
  monthKey: string;
  includedSeconds: number;
  usedSeconds: number;
  overageSeconds: number;
  remainingSeconds: number;
};

export type FaceSwapJobRecord = {
  id: string;
  subreddit: string;
  userId: string;
  status: FaceSwapJobStatus;
  sourceImageUrl: string;
  targetUrl: string;
  targetType: FaceSwapTargetType;
  createdAt: number;
  startedAt?: number;
  completedAt?: number;
  progressPct?: number;
  outputUrl?: string;
  outputExpiresAt?: number;
  failureCode?: FaceSwapErrorCode;
  failureMessage?: string;
  estimatedUsageSeconds?: number;
  actualUsageSeconds?: number;
  policyAcceptedAt?: number;
  options?: FaceSwapCreateJobRequest['options'];
};

export type FaceSwapCreateJobResponse = {
  job: FaceSwapJobRecord;
  usage: FaceSwapUsageLedger;
};

export type FaceSwapGetJobResponse = {
  job: FaceSwapJobRecord;
  usage: FaceSwapUsageLedger;
};

export type FaceSwapGetResultResponse = {
  jobId: string;
  outputUrl: string;
  expiresAt?: number;
};

export type PremiumPlanTier = 'free' | 'starter' | 'pro';

export type FaceSwapPlanTier = PremiumPlanTier;

export type PremiumBillingProvider = 'none' | 'nowpayments';

export type FaceSwapBillingProvider = PremiumBillingProvider;

export type FaceSwapNowSubscriptionStatus = 'WAITING_PAY' | 'PAID' | 'PARTIALLY_PAID' | 'EXPIRED';

export type PremiumBillingStatusResponse = {
  tier: PremiumPlanTier;
  billingProvider: PremiumBillingProvider;
  nowSubscriptionId?: string;
  nowSubscriptionPlanId?: string;
  nowSubscriptionStatus?: FaceSwapNowSubscriptionStatus;
  nowIsActive?: boolean;
  subscriberEmail?: string;
  billingPartnerId?: string;
  subscriptionStatus?: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: number;
  gems: number;
  monthlyGems: number;
  planDisplayName: string;
  usage: FaceSwapUsageLedger;
};

export type FaceSwapBillingStatusResponse = PremiumBillingStatusResponse;

export type FaceSwapSampleAsset = {
  id: string;
  title: string;
  thumbnailUrl: string;
  mediaUrl: string;
  durationSeconds?: number;
};

export type FaceSwapSampleResult = {
  id: string;
  sourceImageId: string;
  targetVideoId: string;
  outputType: FaceSwapTargetType;
  outputUrl: string;
  thumbnailUrl?: string;
  durationSeconds?: number;
};

export type FaceSwapSampleManifestResponse = {
  sourceImages: FaceSwapSampleAsset[];
  targetVideos: FaceSwapSampleAsset[];
  results: FaceSwapSampleResult[];
  simulation: {
    queuedMs: number;
    runningMs: number;
    uploadingMs: number;
  };
};

export type PremiumPlanComparison = {
  tier: Exclude<PremiumPlanTier, 'free'>;
  displayName: string;
  monthlyPriceCents: number;
  currency: string;
  includedSeconds: number;
  overageCentsPerMinute: number;
  monthlyGems: number;
  benefits: string[];
};

export type FaceSwapPlanComparison = PremiumPlanComparison;

export type PremiumPlanComparisonResponse = {
  sampleTierLabel: string;
  storefrontTitle: string;
  storefrontDescription: string;
  plans: PremiumPlanComparison[];
};

export type FaceSwapPlanComparisonResponse = PremiumPlanComparisonResponse;

export type PremiumCheckoutRequest = {
  tier: Exclude<PremiumPlanTier, 'free'>;
  returnPath?: string;
};

export type FaceSwapCheckoutRequest = PremiumCheckoutRequest;

export type PremiumCheckoutResponse = {
  status: 'ok';
  subscriptionId: string;
  subscriptionStatus: FaceSwapNowSubscriptionStatus;
  isActive: boolean;
  subscriberEmail: string;
  expiresAt?: number;
  message: string;
};

export type FaceSwapCheckoutResponse = PremiumCheckoutResponse;

export type PremiumCancelSubscriptionRequest = {
  atPeriodEnd: true;
};

export type FaceSwapCancelSubscriptionRequest = PremiumCancelSubscriptionRequest;

export type PremiumCancelSubscriptionResponse = {
  status: 'ok';
  billing: PremiumBillingStatusResponse;
};

export type FaceSwapCancelSubscriptionResponse = PremiumCancelSubscriptionResponse;

export type PremiumCheckoutSyncRequest = {
  subscriptionId?: string;
};

export type FaceSwapCheckoutSyncRequest = PremiumCheckoutSyncRequest;

export type PremiumCheckoutSyncResponse = {
  status: 'ok';
  billing: PremiumBillingStatusResponse;
};

export type FaceSwapCheckoutSyncResponse = PremiumCheckoutSyncResponse;

export type FaceSwapWorkerClaimRequest = {
  workerId: string;
  capabilities?: string[];
};

export type FaceSwapWorkerClaimResponse = {
  job: FaceSwapJobRecord | null;
};

export type FaceSwapWorkerProgressRequest = {
  progressPct: number;
  stage?: 'download' | 'extract' | 'swap' | 'encode' | 'upload';
};

export type FaceSwapWorkerCompleteRequest = {
  outputUrl: string;
  actualUsageSeconds: number;
  outputExpiresAt?: number;
};

export type FaceSwapWorkerFailRequest = {
  failureCode?: FaceSwapErrorCode;
  failureMessage: string;
};

export type FaceSwapPolicyAcceptRequest = {
  accepted: boolean;
};

export type FaceSwapPolicyAcceptResponse = {
  accepted: boolean;
  acceptedAt?: number;
};

export type FaceSwapAdminMetricsResponse = {
  totalJobs: number;
  queuedJobs: number;
  runningJobs: number;
  completedJobs: number;
  failedJobs: number;
  avgDurationMs: number;
  avgQueueDelayMs: number;
};

export type UserLevelState = LevelProgress;

export type LevelPayload = {
  userLevel: UserLevelState;
};

export const isCompleteAssignment = (
  assignment: FMKAssignment,
  slots: FMKSlot[]
): assignment is FMKAssignment => {
  const slotIds = new Set(slots.map((slot) => slot.id));
  const choices = [assignment.f, assignment.m, assignment.k];
  return (
    new Set(choices).size === 3 &&
    choices.every((choice) => typeof choice === 'string' && slotIds.has(choice))
  );
};

export const buildEmptyTotals = (slots: FMKSlot[]): RoleTotals => {
  const zeroed = Object.fromEntries(slots.map((slot) => [slot.id, 0]));
  return {
    f: { ...zeroed },
    m: { ...zeroed },
    k: { ...zeroed },
  };
};

export const toPercentages = (totals: RoleTotals, totalVotes: number): FMKPercentages => {
  if (totalVotes <= 0) {
    return {
      f: Object.fromEntries(Object.keys(totals.f).map((key) => [key, 0])),
      m: Object.fromEntries(Object.keys(totals.m).map((key) => [key, 0])),
      k: Object.fromEntries(Object.keys(totals.k).map((key) => [key, 0])),
    };
  }
  const toPct = (counts: Record<string, number>) =>
    Object.fromEntries(
      Object.entries(counts).map(([slotId, count]) => [
        slotId,
        Math.round((count / totalVotes) * 100),
      ])
    );

  return {
    f: toPct(totals.f),
    m: toPct(totals.m),
    k: toPct(totals.k),
  };
};

export const pickMajority = (totals: RoleTotals, slots: FMKSlot[]): FMKAssignment => {
  const orderedIds = slots.map((slot) => slot.id).filter((id): id is string => Boolean(id));
  const fallbackId = orderedIds[0] ?? '';
  const pick = (counts: Record<string, number>) =>
    orderedIds.reduce<string>((winner, current) => {
      const winnerCount = counts[winner] ?? 0;
      const currentCount = counts[current] ?? 0;
      if (currentCount > winnerCount) return current;
      return winner;
    }, fallbackId);

  return {
    f: pick(totals.f),
    m: pick(totals.m),
    k: pick(totals.k),
  };
};
